/**
 * Loads a Mapbox style with unavailable sources stripped.
 *
 * The navigation styles bundle `mapbox-traffic-v1` / `mapbox-incidents-v1`,
 * which are a paid entitlement - free accounts get a 404 for every such tile,
 * flooding the console and burning head-unit bandwidth on requests that can
 * never succeed. Route congestion colouring is unaffected (it comes from the
 * Directions API), so we simply remove those sources and their layers before
 * handing the style to the map.
 *
 * Falls back to the plain style URL if the fetch fails (map still works,
 * just with the 404 noise).
 */

import { MAPBOX_TOKEN, MAP_STYLES } from '../core/config';

type Theme = keyof typeof MAP_STYLES;

const STRIP_SOURCES = new Set(['mapbox-traffic', 'mapbox-incidents']);

const cache = new Map<Theme, object>();

export async function loadStrippedStyle(theme: Theme): Promise<object | string> {
  const cached = cache.get(theme);
  if (cached) return cached;

  const url = MAP_STYLES[theme];
  try {
    const httpUrl =
      url.replace('mapbox://styles/', 'https://api.mapbox.com/styles/v1/') +
      `?access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(httpUrl, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return url;
    const style = (await res.json()) as {
      sources?: Record<string, unknown>;
      layers?: Array<{ source?: string }>;
    };
    if (style.sources) {
      for (const name of STRIP_SOURCES) delete style.sources[name];
    }
    if (style.layers) {
      style.layers = style.layers.filter((l) => !l.source || !STRIP_SOURCES.has(l.source));
    }
    cache.set(theme, style as object);
    return style as object;
  } catch {
    return url; // offline or blocked - let mapbox-gl load the URL itself
  }
}
